import type { LocalIfcObservation } from "../integration/local-ifc";
import type { DraftEntry } from "./draft";
import { threeWayAccounting, type ThreeWayAccounting } from "./coverage";
import { buildInspectionHierarchy } from "./hierarchy";
import type { FixtureElement } from "./types";

export type MeasurementKind = "count" | "length" | "area" | "volume";

export interface ReportMeasurement {
  readonly kind: MeasurementKind;
  /** The unit as reported by the source quantity set, or null when no unit accompanied the value. */
  readonly unit: string | null;
  readonly measuredValue: number;
  readonly measuredCount: number;
  readonly unmeasuredCount: number;
  readonly unsupportedUnitCount: number;
  readonly unitConflict: boolean;
}

/**
 * The two built-in grouping keys mirror the D0-decided Building／Floor and IFC class／type levels.
 * Every other key is `property:<name>`, derived only from property names that already exist on at
 * least one element of the active dataset (see `availableGroupingKeys`) — no classification logic is
 * invented here.
 */
export type BuiltinReportGroupingKeyId = "scope" | "type";
export type ReportGroupingKeyId = BuiltinReportGroupingKeyId | `property:${string}`;

export interface ReportGroupingKeyDescriptor {
  readonly id: ReportGroupingKeyId;
  readonly label: string;
}

export const DEFAULT_REPORT_GROUPING_RULES: readonly ReportGroupingKeyId[] = ["scope", "type"];

/** A checked guard against pathological fan-out from stacking many high-cardinality property
 * levels; not a claimed product limit (mirrors the `checkedCapIsProductionLimit: false` convention
 * used by the D2 local proposal contracts). */
export const REPORT_GROUPING_RULE_CAP = 8 as const;

function builtinGroupingKeyLabel(id: BuiltinReportGroupingKeyId): string {
  return id === "scope" ? "Building/Floor" : "IFC class/type";
}

export function groupingKeyLabel(id: ReportGroupingKeyId): string {
  if (id === "scope" || id === "type") return builtinGroupingKeyLabel(id);
  return id.slice("property:".length);
}

/**
 * Enumerates the grouping keys that can be derived, without invention, from the active dataset:
 * the two built-in levels plus every property name observed on at least one element. Order is
 * deterministic (built-ins first, then property names sorted lexicographically) so the same
 * dataset always offers the same key list in the same order.
 */
export function availableGroupingKeys(elements: readonly FixtureElement[]): readonly ReportGroupingKeyDescriptor[] {
  const propertyNames = new Set<string>();
  for (const element of elements) {
    for (const key of Object.keys(element.properties)) propertyNames.add(key);
  }
  const ids: readonly ReportGroupingKeyId[] = [
    "scope",
    "type",
    ...[...propertyNames].sort((left, right) => left.localeCompare(right)).map((name) => `property:${name}` as const)
  ];
  return ids.map((id) => ({ id, label: groupingKeyLabel(id) }));
}

function assertKnownGroupingKey(keyId: ReportGroupingKeyId, available: readonly ReportGroupingKeyDescriptor[]): void {
  if (!available.some((descriptor) => descriptor.id === keyId)) {
    throw new RangeError(`Grouping key "${keyId}" is not derived from the active dataset.`);
  }
}

/** Appends a grouping level. A key already present is left where it is (no duplicate level, no
 * reorder-as-a-side-effect); adding a key beyond `REPORT_GROUPING_RULE_CAP` or one that is not
 * derivable from the active dataset is refused. */
export function addGroupingRule(
  rules: readonly ReportGroupingKeyId[],
  keyId: ReportGroupingKeyId,
  available: readonly ReportGroupingKeyDescriptor[]
): readonly ReportGroupingKeyId[] {
  assertKnownGroupingKey(keyId, available);
  if (rules.includes(keyId)) return rules;
  if (rules.length >= REPORT_GROUPING_RULE_CAP) {
    throw new RangeError(`Report grouping supports at most ${REPORT_GROUPING_RULE_CAP} ordered levels.`);
  }
  return [...rules, keyId];
}

/** Removing a key that is not present is a no-op rather than a refusal, so the UI can call this
 * unconditionally when a level is dismissed. */
export function removeGroupingRule(
  rules: readonly ReportGroupingKeyId[],
  keyId: ReportGroupingKeyId
): readonly ReportGroupingKeyId[] {
  return rules.filter((id) => id !== keyId);
}

/** Swaps a grouping level with its neighbour. Moving the first level up, or the last level down,
 * is a no-op rather than wrapping around or throwing. */
export function moveGroupingRule(
  rules: readonly ReportGroupingKeyId[],
  keyId: ReportGroupingKeyId,
  direction: "up" | "down"
): readonly ReportGroupingKeyId[] {
  const index = rules.indexOf(keyId);
  if (index === -1) return rules;
  const target = direction === "up" ? index - 1 : index + 1;
  if (target < 0 || target >= rules.length) return rules;
  const next = [...rules];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** A checked guard mirroring `REPORT_GROUPING_RULE_CAP`, for the same "bounded fan-out, not a
 * claimed product limit" reason — a display column is a different concept from a grouping level,
 * but the same pathological-fan-out risk applies to stacking many of them. */
export const REPORT_DISPLAY_COLUMN_CAP = 8 as const;

/**
 * Enumerates the property names a display column can be added for, derived only from the active
 * dataset. This deliberately reuses `availableGroupingKeys` as the single enumeration source
 * (rather than re-walking `elements`) and keeps only its `property:<name>` entries, stripped back
 * to the bare name — the same "properties observed on at least one element" set the grouping-level
 * picker already offers, just without the two built-in scope/type levels that make no sense as a
 * display column.
 */
export function availableDisplayColumns(elements: readonly FixtureElement[]): readonly string[] {
  return availableGroupingKeys(elements)
    .filter((descriptor) => descriptor.id.startsWith("property:"))
    .map((descriptor) => descriptor.label);
}

/** Appends a display column. A column already present is left where it is (no duplicate column);
 * adding one beyond `REPORT_DISPLAY_COLUMN_CAP` or one that is not derivable from the active
 * dataset is refused, mirroring `addGroupingRule`. */
export function addDisplayColumn(
  columns: readonly string[],
  propertyName: string,
  available: readonly string[]
): readonly string[] {
  if (!available.includes(propertyName)) {
    throw new RangeError(`Display column "${propertyName}" is not derived from the active dataset.`);
  }
  if (columns.includes(propertyName)) return columns;
  if (columns.length >= REPORT_DISPLAY_COLUMN_CAP) {
    throw new RangeError(`Report supports at most ${REPORT_DISPLAY_COLUMN_CAP} display columns.`);
  }
  return [...columns, propertyName];
}

/** Removing a column that is not present is a no-op rather than a refusal, mirroring
 * `removeGroupingRule` so the UI can call this unconditionally when a column chip is dismissed. */
export function removeDisplayColumn(columns: readonly string[], propertyName: string): readonly string[] {
  return columns.filter((name) => name !== propertyName);
}

export interface ReportGroupPathSegment {
  readonly keyId: ReportGroupingKeyId;
  readonly label: string;
  readonly value: string;
}

export interface ReportGroupRow {
  readonly groupId: string;
  readonly path: readonly ReportGroupPathSegment[];
  readonly records: number;
  readonly elementIds: readonly string[];
  readonly accounting: ThreeWayAccounting;
  readonly measurement: ReportMeasurement;
  /** One computed value per active display column (see `ReportColumnValue`), keyed by the bare
   * property name — never by a `property:<name>` grouping key id, because a display column is a
   * distinct concept from a grouping level (see the module doc comment above `ReportColumnValue`). */
  readonly columnValues: ReadonlyMap<string, ReportColumnValue>;
}

export interface Report {
  readonly datasetId: string;
  readonly datasetLabel: string;
  readonly groupingRules: readonly ReportGroupingKeyId[];
  /** Property names shown as extra per-group columns, independent of `groupingRules` — adding one
   * does not change how records are partitioned into groups (see `ReportColumnValue`). */
  readonly displayColumns: readonly string[];
  readonly groups: readonly ReportGroupRow[];
}

/**
 * A "display column" shows a chosen property's value alongside an already-grouped row, without
 * affecting how records are partitioned (that remains `groupingRules`'s job). Unlike a grouping
 * key, whose "(missing)" bucket only ever describes one element at a time, a display column must
 * summarize the property across every element in the group without inventing a single value, so
 * it is a tri-state:
 * - `single`: every element in the group carries the property, and they all agree on its value.
 * - `mixed`: elements disagree — either on the value itself, or on whether the property exists at
 *   all (an element that lacks the property counts as one more distinct outcome, alongside however
 *   many distinct present values there are, so a mostly-uniform-but-partially-absent property is
 *   never misreported as `single`).
 * - `absent`: no element in the group carries the property at all. Deliberately not called
 *   `unmeasured` — that term already means "no source IFC quantity", a measurement concept, not a
 *   property concept — and deliberately a distinct case from `mixed`, so a caller can never treat
 *   "nobody has this" and "everybody disagrees" as the same signal.
 */
export type ReportColumnValue =
  | { readonly kind: "single"; readonly value: string }
  | { readonly kind: "mixed"; readonly distinctValueCount: number }
  | { readonly kind: "absent" };

/** Renders a `ReportColumnValue` as the one string shown in both the Report table cell and the CSV
 * export, so the two surfaces can never drift on how "mixed"／"absent" read. */
export function formatReportColumnValue(value: ReportColumnValue): string {
  if (value.kind === "single") return value.value;
  if (value.kind === "mixed") return `mixed (${value.distinctValueCount} values)`;
  return "(absent)";
}

const QUANTITY_DATA_TYPES: Readonly<Record<string, MeasurementKind>> = {
  IFCQUANTITYLENGTH: "length",
  IFCQUANTITYAREA: "area",
  IFCQUANTITYVOLUME: "volume",
  IFCQUANTITYCOUNT: "count"
};

/**
 * Default measurement kind per IFC class/type. This mirrors Qonic's public Report Panel
 * documentation (see research/qonic-informed-data-tools.md, "2026-08-11 Report Panel／Quantity
 * Takeoff再調査"): Net Area for Curtain Wall/Roof/Wall, Net Volume for Beam/Footing/Slab/Column,
 * Length for linear MEP segments, Count for everything else. This is a Workbench display default
 * mirroring public documentation, not a BIM-Hub- or IFC-authoritative classification.
 */
export function defaultMeasurementKindForType(type: string): MeasurementKind {
  if (/^(Wall|Roof|CurtainWall)$/.test(type)) return "area";
  if (/^(Beam|Footing|Slab|Column)$/.test(type)) return "volume";
  if (/(Segment|Duct|Pipe|Cable|Conduit|Run|Wire)/i.test(type)) return "length";
  return "count";
}

/**
 * A group can, under custom grouping, mix more than one IFC class/type (e.g. grouping by a
 * property level without also grouping by type). When every element in the group shares one type,
 * that type's documented default is used as before; a genuinely mixed-type group falls back to
 * Count, the same "everything else" default `defaultMeasurementKindForType` already uses, rather
 * than guessing which type's unit should win.
 */
function measurementKindForGroup(elements: readonly FixtureElement[]): MeasurementKind {
  const types = new Set(elements.map((element) => element.type));
  return types.size === 1 ? defaultMeasurementKindForType([...types][0]!) : "count";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function items(value: unknown): readonly unknown[] {
  return Array.isArray(value) ? value : [];
}

interface SourceQuantity {
  readonly kind: MeasurementKind;
  readonly value: number;
  readonly unit: string | null;
}

/**
 * Indexes the Plan's `properties` associations by element_id, keeping only quantity-set (`Qto_*`)
 * entries whose `data_type` matches a known IFC quantity value type. An element_id carrying more
 * than one candidate for the same MeasurementKind is treated as ambiguous and excluded — mirroring
 * the unique-occurrence-property overlay rule already used for Pset properties in
 * local-ifc-projection.ts — rather than guessing which one is authoritative.
 */
function sourceQuantitiesByElementId(
  plan: Record<string, unknown>
): ReadonlyMap<string, ReadonlyMap<MeasurementKind, SourceQuantity>> {
  const result = new Map<string, Map<MeasurementKind, SourceQuantity>>();
  const ambiguous = new Map<string, Set<MeasurementKind>>();
  for (const rawAssoc of items(plan.properties)) {
    if (!isRecord(rawAssoc) || typeof rawAssoc.element_id !== "string" || rawAssoc.element_id === "") continue;
    const elementId = rawAssoc.element_id;
    for (const rawSet of items(rawAssoc.sets)) {
      if (!isRecord(rawSet) || typeof rawSet.name !== "string" || !/^qto_/i.test(rawSet.name)) continue;
      for (const rawProperty of items(rawSet.props)) {
        if (!isRecord(rawProperty) || typeof rawProperty.data_type !== "string") continue;
        const kind = QUANTITY_DATA_TYPES[rawProperty.data_type.toUpperCase()];
        if (kind === undefined || typeof rawProperty.value !== "number" || !Number.isFinite(rawProperty.value)) continue;
        const unit = typeof rawProperty.unit === "string" && rawProperty.unit !== "" ? rawProperty.unit : null;
        const perElement = result.get(elementId) ?? new Map<MeasurementKind, SourceQuantity>();
        const perElementAmbiguous = ambiguous.get(elementId) ?? new Set<MeasurementKind>();
        if (perElement.has(kind)) perElementAmbiguous.add(kind);
        else perElement.set(kind, { kind, value: rawProperty.value, unit });
        result.set(elementId, perElement);
        ambiguous.set(elementId, perElementAmbiguous);
      }
    }
  }
  for (const [elementId, kinds] of ambiguous) {
    const perElement = result.get(elementId);
    if (perElement === undefined) continue;
    for (const kind of kinds) perElement.delete(kind);
  }
  return result;
}

function sourceIdentityCandidates(element: FixtureElement): readonly string[] {
  const sourceRecord = isRecord(element.unknown.sourceRecord) ? element.unknown.sourceRecord : null;
  if (sourceRecord === null) return [];
  return [...new Set([sourceRecord.id, sourceRecord.guid].filter((value): value is string => typeof value === "string" && value !== ""))];
}

function quantityForElement(
  element: FixtureElement,
  kind: MeasurementKind,
  index: ReadonlyMap<string, ReadonlyMap<MeasurementKind, SourceQuantity>> | null
): SourceQuantity | null {
  if (index === null) return null;
  for (const candidate of sourceIdentityCandidates(element)) {
    const quantity = index.get(candidate)?.get(kind);
    if (quantity !== undefined) return quantity;
  }
  return null;
}

/**
 * Only the D0 synthetic fixture (no local IFC observation at all) trusts `FixtureElement.quantities`
 * as a declared source value. When a local IFC observation is present, `quantities` may hold the
 * plan-derived box-projection approximation (Wall Length/Area/Volume in local-ifc-projection.ts) and
 * must never be treated as a source IFC quantity set.
 */
function fixtureQuantityFallback(element: FixtureElement, kind: MeasurementKind): SourceQuantity | null {
  const key = kind === "length" ? "Length" : kind === "area" ? "Area" : kind === "volume" ? "Volume" : null;
  if (key === null) return null;
  const value = element.quantities[key];
  return typeof value === "number" && Number.isFinite(value) ? { kind, value, unit: null } : null;
}

function measuredQuantity(
  element: FixtureElement,
  kind: MeasurementKind,
  index: ReadonlyMap<string, ReadonlyMap<MeasurementKind, SourceQuantity>> | null,
  allowFixtureQuantityFallback: boolean
): SourceQuantity | null {
  const sourced = quantityForElement(element, kind, index);
  if (sourced !== null) return sourced;
  return allowFixtureQuantityFallback ? fixtureQuantityFallback(element, kind) : null;
}

function measurementForGroup(
  kind: MeasurementKind,
  elements: readonly FixtureElement[],
  index: ReadonlyMap<string, ReadonlyMap<MeasurementKind, SourceQuantity>> | null,
  allowFixtureQuantityFallback: boolean
): ReportMeasurement {
  if (kind === "count") {
    return {
      kind,
      unit: null,
      measuredValue: elements.length,
      measuredCount: elements.length,
      unmeasuredCount: 0,
      unsupportedUnitCount: 0,
      unitConflict: false
    };
  }
  const matched: SourceQuantity[] = [];
  let unmeasuredCount = 0;
  for (const element of elements) {
    const quantity = measuredQuantity(element, kind, index, allowFixtureQuantityFallback);
    if (quantity === null) unmeasuredCount += 1;
    else matched.push(quantity);
  }
  const units = new Set(matched.map((quantity) => quantity.unit).filter((unit): unit is string => unit !== null));
  if (units.size > 1) {
    // Two or more source quantities disagree on unit; summing them would require an unrequested
    // conversion, so the group is reported as explicitly unsupported instead.
    return {
      kind,
      unit: null,
      measuredValue: 0,
      measuredCount: 0,
      unmeasuredCount,
      unsupportedUnitCount: matched.length,
      unitConflict: true
    };
  }
  return {
    kind,
    unit: units.size === 1 ? [...units][0]! : null,
    measuredValue: matched.reduce((total, quantity) => total + quantity.value, 0),
    measuredCount: matched.length,
    unmeasuredCount,
    unsupportedUnitCount: 0,
    unitConflict: false
  };
}

interface GroupBucketValue {
  readonly id: string;
  readonly label: string;
}

/**
 * Reuses `buildInspectionHierarchy` (dataset → scope → type) purely to read off each element's
 * Building/Floor scope assignment, so the "scope" grouping key never re-implements that
 * classifier — it is a lookup over the same tree the Browse/Model-tree side already builds.
 */
function scopeMapFor(
  datasetId: string,
  datasetLabel: string,
  elements: readonly FixtureElement[]
): ReadonlyMap<string, GroupBucketValue> {
  const hierarchy = buildInspectionHierarchy(datasetId, datasetLabel, elements);
  const map = new Map<string, GroupBucketValue>();
  for (const scope of hierarchy.children) {
    for (const id of scope.elementIds) map.set(id, { id: scope.id, label: scope.label });
  }
  return map;
}

function stringifyPropertyValue(value: unknown): string {
  if (value === undefined) return "(missing)";
  if (value === null) return "(null)";
  if (typeof value === "string") return value === "" ? "(empty)" : value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return "(unrepresentable)";
  }
}

function groupBucketValue(
  element: FixtureElement,
  keyId: ReportGroupingKeyId,
  scopeMap: ReadonlyMap<string, GroupBucketValue>
): GroupBucketValue {
  if (keyId === "scope") return scopeMap.get(element.id) ?? { id: "unscoped", label: "Unscoped" };
  // `element.type` is derived from a plan collection name (see collectionType() in
  // local-ifc-projection.ts) and, unlike almost every other string this importer produces, is not
  // routed through a non-empty fallback. An empty type previously rendered as a literal blank
  // table cell here — a genuinely missing record misread as a UI/CSS glitch (kaz, 2026-08-11).
  if (keyId === "type") return element.type === "" ? { id: "(missing type)", label: "(missing type)" } : { id: element.type, label: element.type };
  const propertyName = keyId.slice("property:".length);
  const hasProperty = Object.prototype.hasOwnProperty.call(element.properties, propertyName);
  const label = hasProperty ? stringifyPropertyValue(element.properties[propertyName]) : "(missing)";
  return { id: label, label };
}

/**
 * Computes the tri-state `ReportColumnValue` for one property over one group's elements. Presence
 * is checked with `hasOwnProperty`, the same convention `groupBucketValue` already uses for the
 * "(missing)" grouping bucket, so a property explicitly set to `null`/`undefined` counts as present
 * (and is stringified by `stringifyPropertyValue` like anywhere else), while a key that was never
 * set at all does not. An element lacking the property counts as one more distinct outcome once at
 * least one other element has it — see the `ReportColumnValue` doc comment for why a
 * partially-absent property is `mixed`, not `single`.
 */
function reportColumnValueForGroup(elements: readonly FixtureElement[], propertyName: string): ReportColumnValue {
  const presentValues = new Set<string>();
  let presentCount = 0;
  let absentCount = 0;
  for (const element of elements) {
    if (Object.prototype.hasOwnProperty.call(element.properties, propertyName)) {
      presentCount += 1;
      presentValues.add(stringifyPropertyValue(element.properties[propertyName]));
    } else {
      absentCount += 1;
    }
  }
  if (presentCount === 0) return { kind: "absent" };
  if (absentCount === 0 && presentValues.size === 1) {
    return { kind: "single", value: [...presentValues][0]! };
  }
  return { kind: "mixed", distinctValueCount: presentValues.size + (absentCount > 0 ? 1 : 0) };
}

function columnValuesForGroup(
  elements: readonly FixtureElement[],
  displayColumns: readonly string[]
): ReadonlyMap<string, ReportColumnValue> {
  return new Map(displayColumns.map((propertyName) => [propertyName, reportColumnValueForGroup(elements, propertyName)] as const));
}

interface Bucket {
  readonly path: readonly ReportGroupPathSegment[];
  readonly elements: readonly FixtureElement[];
}

/**
 * Partitions `elements` by the ordered grouping keys. Each key strictly refines the partition
 * produced by the previous ones, so the resulting leaves — and therefore which elements end up
 * together — depend only on the *set* of keys in `rules`, never on their order; only the displayed
 * `path` (and consequently `groupId`) reflects the order. Bucket values are sorted by their stable
 * id at every level, so repeated builds of the same input are byte-identical.
 */
function buildBuckets(
  elements: readonly FixtureElement[],
  rules: readonly ReportGroupingKeyId[],
  scopeMap: ReadonlyMap<string, GroupBucketValue>
): readonly Bucket[] {
  let buckets: readonly Bucket[] = [{ path: [], elements }];
  for (const keyId of rules) {
    const keyLabel = groupingKeyLabel(keyId);
    const next: Bucket[] = [];
    for (const bucket of buckets) {
      const byValueId = new Map<string, { readonly value: GroupBucketValue; readonly elements: FixtureElement[] }>();
      for (const element of bucket.elements) {
        const value = groupBucketValue(element, keyId, scopeMap);
        const existing = byValueId.get(value.id);
        if (existing === undefined) byValueId.set(value.id, { value, elements: [element] });
        else existing.elements.push(element);
      }
      const ordered = [...byValueId.values()].sort((left, right) => left.value.id.localeCompare(right.value.id));
      for (const entry of ordered) {
        next.push({ path: [...bucket.path, { keyId, label: keyLabel, value: entry.value.label }], elements: entry.elements });
      }
    }
    buckets = next;
  }
  return buckets.filter((bucket) => bucket.elements.length > 0);
}

function groupIdFor(path: readonly ReportGroupPathSegment[]): string {
  // U+0000/U+0001 delimiters avoid collisions between e.g. keyId "type" value "A" and keyId
  // "typeA" value "" -- the same discipline draft.ts uses for its elementId/property map keys.
  return path.length === 0 ? "\u0000all" : path.map((segment) => `${segment.keyId}\u0000${segment.value}`).join("\u0001");
}

/**
 * Builds the report for the given, caller-supplied ordered grouping rules (default: Building／Floor
 * → IFC class／type, matching the first D0 increment) and display columns (default: none). Grouping
 * keys and display columns are both derived only from the dataset itself (see
 * `availableGroupingKeys`／`availableDisplayColumns`); no new classification is invented here.
 * `displayColumns` never affects `buildBuckets`'s partition — it only adds `columnValues` to each
 * already-computed group.
 */
export function buildReport(
  datasetId: string,
  datasetLabel: string,
  elements: readonly FixtureElement[],
  observation: LocalIfcObservation | null,
  groupingRules: readonly ReportGroupingKeyId[] = DEFAULT_REPORT_GROUPING_RULES,
  displayColumns: readonly string[] = []
): Report {
  const scopeMap = scopeMapFor(datasetId, datasetLabel, elements);
  const plan =
    observation !== null && observation.state === "observed" && isRecord(observation.raw) && isRecord(observation.raw.plan)
      ? observation.raw.plan
      : null;
  const index = plan === null ? null : sourceQuantitiesByElementId(plan);
  const allowFixtureQuantityFallback = observation === null;
  const buckets = buildBuckets(elements, groupingRules, scopeMap);
  const groups: ReportGroupRow[] = buckets.map((bucket) => {
    const elementIds = bucket.elements.map((element) => element.id).sort((left, right) => left.localeCompare(right));
    return {
      groupId: groupIdFor(bucket.path),
      path: bucket.path,
      records: bucket.elements.length,
      elementIds,
      accounting: threeWayAccounting(bucket.elements),
      measurement: measurementForGroup(measurementKindForGroup(bucket.elements), bucket.elements, index, allowFixtureQuantityFallback),
      columnValues: columnValuesForGroup(bucket.elements, displayColumns)
    };
  });
  return { datasetId, datasetLabel, groupingRules, displayColumns, groups };
}

function csvField(value: string | number): string {
  const text = String(value);
  return /["\n,]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Local-file CSV export of the currently displayed report. Not a BIM-Hub authoritative receipt or
 * round-trip. Display columns (see `ReportColumnValue`) are appended right after the grouping-rule
 * columns, using the same `formatReportColumnValue` text the Report table cell uses, so the two
 * surfaces can never disagree on how a column reads. */
export function reportToCsv(report: Report): string {
  const header = [
    ...report.groupingRules.map(groupingKeyLabel),
    ...report.displayColumns,
    "Records",
    "Renderable",
    "Unrendered plan evidence",
    "Producer skipped",
    "Measurement kind",
    "Measured value",
    "Unit",
    "Measured count",
    "Unmeasured count",
    "Unsupported unit count"
  ];
  const lines = [header.map(csvField).join(",")];
  for (const group of report.groups) {
    lines.push(
      [
        ...group.path.map((segment) => segment.value),
        ...report.displayColumns.map((name) => formatReportColumnValue(group.columnValues.get(name) ?? { kind: "absent" })),
        group.records,
        group.accounting.renderable,
        group.accounting.unrenderedPlanEvidence,
        group.accounting.producerSkipped,
        group.measurement.kind,
        group.measurement.unitConflict ? "unsupported (mixed units)" : group.measurement.measuredValue,
        group.measurement.unit ?? "",
        group.measurement.measuredCount,
        group.measurement.unmeasuredCount,
        group.measurement.unsupportedUnitCount
      ]
        .map(csvField)
        .join(",")
    );
  }
  return `${lines.join("\r\n")}\r\n`;
}

// --- Pending-changes simulation (before／after, never applied) -----------------------------------

/**
 * Applies only the property-level operations already staged in the session-local Draft ledger
 * (`domain/draft.ts` — the same ledger the D2-C local enrichment/classification proposals bind to)
 * to a copy of `elements`, as a pure "what if this were applied" preview. Nothing is written or
 * saved. Entries are applied uniformly regardless of their `source` (property/code/component),
 * mirroring `draftEntriesToEnrichmentTargets` in integration/local-proposal-binding.ts, which treats
 * every ledger entry as a property operation on the same element/property pair. An entry naming an
 * element that is no longer part of `elements` is ignored rather than fabricating a new record.
 * `remove` deletes the property key; `replace`/`clear` set it to the ledger's recorded `after` value
 * (an empty string for `clear`, matching `stageBulkPropertyChange`).
 */
export function applyDraftEntriesToElements(
  elements: readonly FixtureElement[],
  entries: readonly DraftEntry[]
): readonly FixtureElement[] {
  if (entries.length === 0) return elements;
  const byElementId = new Map<string, DraftEntry[]>();
  for (const entry of entries) {
    const list = byElementId.get(entry.elementId);
    if (list === undefined) byElementId.set(entry.elementId, [entry]);
    else list.push(entry);
  }
  return elements.map((element) => {
    const elementEntries = byElementId.get(element.id);
    if (elementEntries === undefined) return element;
    const properties: Record<string, unknown> = { ...element.properties };
    for (const entry of elementEntries) {
      if (entry.operation === "remove") delete properties[entry.property];
      else properties[entry.property] = entry.after;
    }
    return { ...element, properties };
  });
}

export interface ReportGroupSnapshot {
  readonly records: number;
  readonly accounting: ThreeWayAccounting;
  readonly measurement: ReportMeasurement;
}

export interface ReportGroupDiffRow {
  readonly groupId: string;
  readonly path: readonly ReportGroupPathSegment[];
  /** `null` when this group only exists after the pending changes are (hypothetically) applied. */
  readonly before: ReportGroupSnapshot | null;
  /** `null` when this group only exists before the pending changes are applied — i.e. every element
   * that used to land here moved to a different group under the active grouping rules. */
  readonly after: ReportGroupSnapshot | null;
  readonly recordsDelta: number;
  /** `null` when either side is missing, the measurement kind differs between before and after, or
   * either side is an unsupported-unit conflict — i.e. whenever summing a delta would require
   * inventing a comparison the source data does not support. */
  readonly measuredValueDelta: number | null;
}

export interface ReportDiff {
  readonly datasetId: string;
  readonly groupingRules: readonly ReportGroupingKeyId[];
  readonly pendingChangeCount: number;
  readonly rows: readonly ReportGroupDiffRow[];
}

function snapshotOf(group: ReportGroupRow): ReportGroupSnapshot {
  return { records: group.records, accounting: group.accounting, measurement: group.measurement };
}

/**
 * Simulates the Report as it would read if every entry currently staged in the Draft ledger were
 * applied, and pairs each group with its before/after snapshot. This never mutates the source
 * elements, never saves or sends anything, and only ever changes which group an element falls into
 * when the active `groupingRules` include a property-based level whose value a staged entry
 * changes — Building/Floor and IFC class/type never move, because the Draft ledger cannot stage
 * geometry- or classification-affecting properties (see `draft.ts`'s `geometryProperties` guard).
 */
export function buildReportDiff(
  datasetId: string,
  datasetLabel: string,
  elements: readonly FixtureElement[],
  observation: LocalIfcObservation | null,
  entries: readonly DraftEntry[],
  groupingRules: readonly ReportGroupingKeyId[] = DEFAULT_REPORT_GROUPING_RULES
): ReportDiff {
  const before = buildReport(datasetId, datasetLabel, elements, observation, groupingRules);
  const afterElements = applyDraftEntriesToElements(elements, entries);
  const after = buildReport(datasetId, datasetLabel, afterElements, observation, groupingRules);
  const beforeById = new Map(before.groups.map((group) => [group.groupId, group] as const));
  const afterById = new Map(after.groups.map((group) => [group.groupId, group] as const));
  const groupIds = [...new Set([...beforeById.keys(), ...afterById.keys()])].sort((left, right) => left.localeCompare(right));
  const rows: ReportGroupDiffRow[] = groupIds.map((groupId) => {
    const beforeGroup = beforeById.get(groupId) ?? null;
    const afterGroup = afterById.get(groupId) ?? null;
    const path = (afterGroup ?? beforeGroup)!.path;
    const measuredValueDelta =
      beforeGroup !== null &&
      afterGroup !== null &&
      beforeGroup.measurement.kind === afterGroup.measurement.kind &&
      !beforeGroup.measurement.unitConflict &&
      !afterGroup.measurement.unitConflict
        ? afterGroup.measurement.measuredValue - beforeGroup.measurement.measuredValue
        : null;
    return {
      groupId,
      path,
      before: beforeGroup === null ? null : snapshotOf(beforeGroup),
      after: afterGroup === null ? null : snapshotOf(afterGroup),
      recordsDelta: (afterGroup?.records ?? 0) - (beforeGroup?.records ?? 0),
      measuredValueDelta
    };
  });
  return { datasetId, groupingRules, pendingChangeCount: entries.length, rows };
}
